import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CopyButtonComponent, DrawerResizeDirective } from '@formrails/shared/ui';
import {
  layoutWorkflow,
  nodeTypeLabelFor,
  Workflow,
  WorkflowNode,
  WorkflowNodeRuleType,
  WorkflowNodeType
} from '@formrails/workflow-designer/data-access';

/** A node as the preview lists it: what it is, what it is called, and where it goes next. */
interface PreviewRow {
  id: string;
  name: string;
  typeLabel: string;
  type: WorkflowNodeType;
  successors: { branch: string | null; name: string }[];
  endsHere: boolean;
}

/**
 * Picks an existing workflow to open. Split view: the workflows on the left,
 * the selected one's nodes on the right. Presentation only — the feature owns
 * the store calls behind these inputs and outputs.
 */
@Component({
  selector: 'app-workflow-selector-modal',
  standalone: true,
  imports: [FormsModule, CopyButtonComponent, DrawerResizeDirective],
  templateUrl: './workflow-selector-modal.component.html',
  styleUrl: './workflow-selector-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowSelectorModalComponent {
  readonly workflows = input<Workflow[]>([]);
  readonly loading = input(false);
  readonly error = input<string | null>(null);
  /** The workflow already open in the designer, marked in the list rather than offered again. */
  readonly currentWorkflowId = input<string | null>(null);
  readonly previewNodes = input<WorkflowNode[]>([]);
  readonly previewLoading = input(false);
  readonly previewError = input<string | null>(null);

  readonly previewWorkflow = output<string>();
  readonly openWorkflow = output<Workflow>();
  readonly reload = output<void>();
  readonly closed = output<void>();

  readonly query = signal('');
  readonly selectedId = signal<string | null>(null);

  readonly filtered = computed(() => {
    const query = this.query().trim().toLowerCase();
    if (!query) return this.workflows();
    return this.workflows().filter(
      workflow =>
        workflow.name.toLowerCase().includes(query) ||
        (workflow.description ?? '').toLowerCase().includes(query)
    );
  });

  readonly selected = computed(() => {
    const id = this.selectedId();
    return id ? this.workflows().find(workflow => workflow.id === id) ?? null : null;
  });

  /** Laid out the way the canvas lays it out, so the preview reads in the same order as the designer. */
  readonly previewRows = computed<PreviewRow[]>(() => {
    const nodes = this.previewNodes();
    const names = new Map(nodes.map(node => [node.id, node.name || 'Untitled']));
    const { slots } = layoutWorkflow(nodes);

    return [...nodes]
      .sort((a, b) => {
        const left = slots[a.id];
        const right = slots[b.id];
        if (!left || !right) return 0;
        return left.layer - right.layer || left.row - right.row;
      })
      .map(node => {
        const isCondition =
          node.type === WorkflowNodeType.Rule && node.ruleType === WorkflowNodeRuleType.Condition;
        // A Terminator's successor is itself, which is an end, not an edge.
        const endsHere = node.type === WorkflowNodeType.Terminator;
        const successors: PreviewRow['successors'] = [];
        if (!endsHere && node.successorTrue && names.has(node.successorTrue)) {
          successors.push({
            branch: isCondition ? 'True' : null,
            name: names.get(node.successorTrue) as string
          });
        }
        if (isCondition && node.successorFalse && names.has(node.successorFalse)) {
          successors.push({ branch: 'False', name: names.get(node.successorFalse) as string });
        }
        return {
          id: node.id,
          name: node.name || 'Untitled',
          typeLabel: nodeTypeLabelFor(node),
          type: node.type,
          successors,
          endsHere
        };
      });
  });

  onSelect(workflow: Workflow): void {
    if (this.selectedId() === workflow.id) return;
    this.selectedId.set(workflow.id);
    this.previewWorkflow.emit(workflow.id);
  }

  /** A role="button" row gets no keyboard activation of its own, and it can't be a <button>: it holds one. */
  onRowKeydown(event: KeyboardEvent, workflow: Workflow): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.onSelect(workflow);
  }

  onOpen(): void {
    const workflow = this.selected();
    if (workflow) this.openWorkflow.emit(workflow);
  }

  onRetryPreview(): void {
    const id = this.selectedId();
    if (id) this.previewWorkflow.emit(id);
  }

  onClose(): void {
    this.closed.emit();
  }
}
